/**
 * Canon Fault Filter Utility
 * 
 * Extracts canon faults from enriched analysis data.
 * Filters to only source="analysis_v3" observations.
 * Consolidates duplicates (max severity per fault_id).
 * Preserves backend-provided order (no UI re-sorting).
 */

/**
 * Canon fault structure
 */
export interface CanonFault {
  fault_id: string;
  severity: number;
}

/**
 * Extract canon faults from enriched analysis data
 * 
 * Filters to only source="analysis_v3" faults (canon faults)
 * Consolidates duplicates by taking max severity per fault_id
 * Preserves backend-provided order (no re-sorting)
 * 
 * @param analysisEnriched - The enriched analysis data from backend
 * @returns Array of canon faults in backend order
 */
export function extractCanonFaults(analysisEnriched: any): CanonFault[] {
  const faultMap = new Map<string, { fault_id: string; severity: number; order: number }>();
  let orderIndex = 0;
  
  if (!analysisEnriched?.phase_summaries) {
    return [];
  }
  
  // First pass: collect all canon faults
  // V3 data: observations may not have a 'source' field (they're already V3)
  // V2 data: observations have source='analysis_v2' (we skip these)
  for (const phaseSummary of analysisEnriched.phase_summaries || []) {
    for (const observation of phaseSummary.observations || []) {
      // Include observations that:
      // 1. Have source === 'analysis_v3' (explicit V3), OR
      // 2. Have no source field (assumed V3 data - V3 observations don't always have source)
      // Skip observations with source === 'analysis_v2' (V2 data)
      const isV3 = !observation.source || observation.source === 'analysis_v3';
      const isV2 = observation.source === 'analysis_v2';
      
      if (isV3 && !isV2 && observation.flaw_id) {
        const faultId = observation.flaw_id.toLowerCase().trim();
        const severity = Math.round(Math.max(0, Math.min(3, observation.severity ?? 0)));
        
        // Take max severity per fault_id (matching consolidation logic)
        const existing = faultMap.get(faultId);
        if (!existing || severity > existing.severity) {
          // Preserve first occurrence order
          if (!existing) {
            faultMap.set(faultId, { fault_id: faultId, severity, order: orderIndex++ });
          } else {
            // Update severity but keep original order
            faultMap.set(faultId, { ...existing, severity });
          }
        }
      }
    }
  }
  
  // Convert to array and sort by original order (preserve backend order)
  const faults: CanonFault[] = Array.from(faultMap.values())
    .sort((a, b) => a.order - b.order)
    .map(({ fault_id, severity }) => ({ fault_id, severity }));
  
  return faults;
}

/**
 * Extract canon fault IDs only (without severity)
 * Useful for validation or filtering
 * 
 * @param analysisEnriched - The enriched analysis data from backend
 * @returns Array of canon fault IDs in backend order
 */
export function extractCanonFaultIds(analysisEnriched: any): string[] {
  return extractCanonFaults(analysisEnriched).map(f => f.fault_id);
}

